import { Box, Button, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useGlobals } from 'src/shared/contexts/appContext';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createFormFamilyMember, getFormFamilyMember } from 'src/shared/services/formFamilyMemberService';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import DynamicFormField from 'src/shared/components/form/dynamicFormField';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { toast } from 'react-toastify';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { PermissionEnum } from 'src/shared/models/enums/permissionEnums';
import { Member } from '../models/member';
import { FormFamilyMemberResponse, FormFamilyMemberResponseItem } from '../models/formFamilyMemberResponse';
import { FormFamilyMemberPost, FormFamilyMemberPostItem } from '../models/formFamilyMemberPost';
import { MemberServerErrorSummary } from './memberServerErrorSummary';

type MemberMeFamilyAddMemberModalProps = {
  modalOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  member: Member;
};

export const MemberMeFamilyAddMemberModal = (props: MemberMeFamilyAddMemberModalProps) => {
  const { modalOpen, onClose, onSave, member } = props;
  const { t } = useTranslation(['member']);
  const [loading, setIsLoading] = useState(false);
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const [fieldsReady, setFieldsReady] = useState<boolean>(false);
  const [rowsAndColsOrder, setRowsAndColsOrder] = useState<any[]>([]);

  const { currentMember } = useGlobals();
  const isMounted = useIsMounted();

  // Make a reference to the top portion of the modal content
  // When an error occures, scroll top of modal into view
  const modalTop = useRef<HTMLDivElement>(null);
  const scrollToModalTop = () => modalTop.current && modalTop.current.scrollIntoView();

  const [, setFormSettings] = useState<FormFamilyMemberResponse>();
  const formMethods = useForm<FormFamilyMemberResponse>({
    defaultValues: {
      items: [{}],
    },
  });

  const { control, handleSubmit, reset } = formMethods;

  const { fields } = useFieldArray({
    control,
    name: 'items',
  });

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const result = await getFormFamilyMember(currentMember?.member_id ?? 0, currentMember?.formName || '');
        if (isMounted()) {
          ConsoleHelper.log(result);
          const uniqueRowIds = new Set<number>();
          // Sort by row and column, start by finding all unique rows
          result.data.items
            .filter((object) => object.formKey !== null)
            .forEach((object) => {
              uniqueRowIds.add(object.formSetting.row);
            });
          // add default values for null
          result.data.items.map((item) => {
            const nullCheck = (val: any) => {
              if (val === null) {
                if (typeof item.formValue === 'number') {
                  return 0;
                }
                return '';
              }
              return val;
            };

            return Object.assign(item, { value: nullCheck(item.formValue) });
          });

          interface ColumnOrder {
            cols: string[];
          }

          const mappedFormItems: ColumnOrder[] = [];
          let rowIndex = 0;
          let previousRow = 0;
          const sortedFormItems: FormFamilyMemberResponseItem[] = [];
          Array.from(uniqueRowIds)
            .sort((a, b) => a - b)
            .forEach((x) => {
              const colItems = result.data.items
                .filter((item) => item.formKey && item.formSetting.row === x)
                .sort((c, d) => c.formSetting.col - d.formSetting.col);

              colItems.forEach((child) => {
                sortedFormItems.push(child);
              });

              if (x !== previousRow) {
                rowIndex += 1;
              }

              mappedFormItems[rowIndex] = { cols: colItems.map((y) => y.formKey) };
              previousRow = x;
            });

          setRowsAndColsOrder(mappedFormItems);
          ConsoleHelper.log('sortedFormItems', sortedFormItems);
          reset({ items: sortedFormItems });
          setFieldsReady(true);
          setFormSettings({ items: sortedFormItems } as FormFamilyMemberResponse);
          ConsoleHelper.log('sortedFormItems', sortedFormItems);
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log(e);
      } finally {
        if (isMounted()) setIsLoading(false);
      }
    };
    fetchData();
  }, [currentMember, isMounted, reset]);

  const saveFormData = useCallback(
    async (data: FormFamilyMemberResponse) => {
      ConsoleHelper.log(data);
      setIsLoading(true);
      const formMemberPutItems: FormFamilyMemberPostItem[] = [];

      data.items.forEach((item) => {
        formMemberPutItems.push({
          formKey: item.formKey,
          formValue: Array.isArray(item.value) ? item.value.filter((x) => x !== null).join(',') : item.value,
        });
      });

      const formData: FormFamilyMemberPost = {
        items: formMemberPutItems,
        mainMemberId: member.member_id || 0,
        formName: currentMember?.formName || '',
      };
      ConsoleHelper.log('formData to post', formData);
      try {
        const res = await createFormFamilyMember(formData);
        if (isMounted()) {
          ConsoleHelper.log('RES create', res);
          toast.success(<>{t('member:editMember.saveSuccessMessage')}</>);
          onSave();
        }
      } catch (e: any) {
        if (isMounted()) {
          ConsoleHelper.log('saveFormDataError:', e);
          setServerErrors(e.data);
          scrollToModalTop();
        }
      } finally {
        if (isMounted()) setIsLoading(false);
      }
    },
    [currentMember?.formName, isMounted, member.member_id, onSave, t],
  );

  const onSubmit = useCallback(
    (data: FormFamilyMemberResponse) => {
      saveFormData(data);
    },
    [saveFormData],
  );

  const onHandleSave = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  return (
    <GenericDialog
      open={modalOpen}
      onClose={() => onClose()}
      onSave={() => ConsoleHelper.log('save')}
      dialogTitle={t('member:family.createFamilyMember')}
      padding={0}
      dialogContent={
        <Box
          ref={modalTop}
          sx={{
            position: 'relative',
            border: (theme) => (serverErrors.length > 0 ? `8px solid ${theme.palette.error.main}` : 'none'),
            minHeight: '20rem',
          }}
        >
          <MemberServerErrorSummary
            serverErrors={serverErrors}
            isMember
            overridePermission={PermissionEnum.MemberCreateOverride}
            onClickOverride={() => ConsoleHelper.log('X')}
          />
          <LoadingOverlay loading={loading} />
          <Box sx={{ p: 2 }}>
            <GridWrapper>
              <Grid item xs={12}>
                <FormProvider {...formMethods}>
                  <form onSubmit={handleSubmit(onSubmit)}>
                    {fieldsReady &&
                      fields &&
                      rowsAndColsOrder &&
                      rowsAndColsOrder.map((item) => {
                        return (
                          <Box sx={{ my: 1, mx: 1, mb: 3 }} key={item.cols[0]}>
                            <GridWrapper>
                              {item.cols.map((x) => {
                                const current = fields.filter((y: any) => y.formKey === x)[0];
                                const currentIndex = fields.findIndex((y) => y.id === current.id);
                                return <DynamicFormField key={current.id} formItem={current} index={currentIndex} />;
                              })}
                            </GridWrapper>
                          </Box>
                        );
                      })}
                  </form>
                </FormProvider>
              </Grid>
            </GridWrapper>
          </Box>
        </Box>
      }
      dialogActions={
        <Box display='flex' flexDirection='row' justifyContent='flex-end' width='100%'>
          <Button size='large' disableElevation variant='contained' color='primary' onClick={onHandleSave} disabled={loading}>
            {t('common:button.save')}
          </Button>
        </Box>
      }
    />
  );
};
